import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';

function isChrome(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isChromeLike = /Chrome|CriOS|Chromium/.test(ua);
  const isExcluded = /Edg|Edge|OPR|Opera|Brave|SamsungBrowser|Silk/.test(ua);
  return isChromeLike && !isExcluded;
}

function isMinScreen(): boolean {
  if (typeof window === 'undefined' || !window.screen) return false;
  const sw = window.screen.width || 0;
  const sh = window.screen.height || 0;
  return sw >= 1366 && sh >= 768;
}
if (!isChrome() || !isMinScreen()) {
  renderUnsupportedBrowser();
} else {
  if (environment.production) enableProdMode();
  platformBrowserDynamic().bootstrapModule(AppModule)
    .catch(err => console.log(err));
}

function renderUnsupportedBrowser(): void {
  const url = window.location.href;

  const chromeOk = isChrome();
  const screenOk = isMinScreen();
  
  // Define se é um erro exclusivamente de tela (Hardware) [cite: 2026-02-02]
  const isHardwareError = chromeOk && !screenOk;

  const badgeText = !chromeOk
    ? '⚠️ Navegador não compatível'
    : '⚠️ Tela muito pequena';

  const title = !chromeOk
    ? 'Use o Google Chrome'
    : 'Use uma tela maior (mín. 15")';

  const subtitle = !chromeOk
    ? 'Para garantir precisão no rastreamento ocular, este sistema funciona apenas no <b>Google Chrome</b>. Abra este link no Chrome para continuar.'
    : 'Para garantir precisão no rastreamento ocular, este sistema requer uma tela maior. (Mínimo recomendado: <b>1366×768</b>).';

  const copyMsg = !chromeOk
    ? 'Link copiado! Abra no Chrome e cole na barra de endereços.'
    : 'Link copiado! Abra em um notebook/desktop e cole na barra de endereços.';

  const safeCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      alert(copyMsg);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      alert(copyMsg);
    }
  };

  document.documentElement.style.height = '100%';
  document.body.style.height = '100%';
  document.body.style.margin = '0';

  document.body.innerHTML = `
  <style>
    :root{
      --bg1:#4f7cff; --bg2:#6a5cff; --card: rgba(255,255,255,.92);
      --text:#0f172a; --muted:#475569; --primary:#1d4ed8;
      --radius: 20px; --shadow: 0 20px 50px rgba(2,6,23,.18);
    }
    body{
      margin:0; height:100vh; overflow:hidden;
      font-family: system-ui, -apple-system, sans-serif;
      background: radial-gradient(1200px 700px at 30% 20%, rgba(255,255,255,.22), transparent 60%),
                  linear-gradient(135deg, var(--bg1), var(--bg2));
      display:flex; align-items:center; justify-content:center;
      padding: 22px;
    }
    .card{
      width:min(600px, 92vw); background: var(--card);
      border-radius: var(--radius); box-shadow: var(--shadow);
      padding: 30px; text-align:center; backdrop-filter: blur(10px);
    }
    .logo-area{ display:flex; align-items:center; justify-content:center; gap:10px; margin-bottom:18px; }
    .logo-area img{ width:160px; max-width: 70%; }
    .badge{ 
      display:inline-block; padding:7px 14px; background:rgba(245,158,11,.12); 
      border:1px solid rgba(245,158,11,.30); color:#92400e; border-radius:999px; 
      font-size:12px; font-weight:800;
    }
    h2{ margin: 16px 0 6px 0; color: var(--text); font-size: 22px; letter-spacing: -0.2px; }
    .subtitle{ color:var(--muted); font-size:14px; line-height: 1.5; margin: 0 0 6px 0; }
    .urlbox{
      margin: 18px auto 14px auto; padding:12px 14px; background:rgba(2,6,23,.05);
      border: 1px solid rgba(2,6,23,.10); border-radius:12px; 
      font-size:12px; word-break:break-all; color:var(--text);
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Courier New", monospace;
      text-align: center;
    }
    .btn{
      background:#1d4ed8; max-width: 420px; color:white; border:none; padding:14px 18px; width: 100%;
      border-radius:12px; font-weight:800; cursor:pointer; transition: background 0.2s;
    }
    .btn:hover{ background: #1541b8; }
    .hint{ margin-top: 12px; font-size: 12px; color: rgba(71,85,105,.95); }
    .footer-logo{ margin-top:22px; border-top:1px solid rgba(0,0,0,.08); padding-top:14px; display:flex; justify-content:center; }
    .colibri-logo{ height:34px; opacity:0.85; display:block; }
  </style>

  <div class="card">
    <div class="logo-area">
      <img src="/assets/link_logo.svg" onerror="this.style.display='none'"/>
    </div>
    
    <div class="badge">${badgeText}</div>
    <h2>${title}</h2>
    <p class="subtitle">${subtitle}</p>
    
    ${!isHardwareError ? `
      <div class="urlbox">${url}</div>
      <button class="btn" id="copyBtn">Copiar link</button>
   
    ` : `
      <p style="margin-top: 20px; font-size: 13px; color: var(--primary); font-weight: 700;">
        Resolução atual: ${window.innerWidth}px
      </p>
    `}
    
    <div class="footer-logo">
      <img src="/assets/colibrilogo.png" class="colibri-logo" alt="Colibri" />
    </div>
  </div>
  `;

  // Previne erro ao tentar adicionar listener em botão inexistente [cite: 2026-02-02]
  if (!isHardwareError) {
    document.getElementById('copyBtn')?.addEventListener('click', safeCopy);
  }
}